/**
 * Los ajustes que el profesorado cambia **desde dentro de la sala**, con la clase delante.
 *
 * Son un cuarto nivel por encima de la cascada de `quiz-rules`, y esa posición es lo único que
 * los hace útiles: la cascada da prioridad absoluta a la pregunta sobre el cuestionario, y la
 * casilla del editor **nunca escribe nulo** —escribe siempre `true` o `false`—, así que un
 * interruptor colocado al nivel del cuestionario no afectaría a ninguna pregunta cuya casilla se
 * hubiera tocado alguna vez. Un interruptor que a veces no hace nada, y sin decir cuándo, es peor
 * que no tenerlo.
 *
 * Por eso la sala manda sobre los tres niveles de abajo cuando dice algo, y `null` significa «no
 * lo he tocado, que mande el cuestionario».
 *
 * Los dos ajustes están aquí y no en el editor del cuestionario por una razón práctica: el
 * profesor decide estas dos cosas **cuando ve quién ha entrado**, no un martes por la tarde
 * preparando material. Y porque hoy los ajustes de cuestionario solo existen si el cuestionario
 * vino de un archivo JSON: uno creado desde la interfaz nace sin ninguno.
 */
import { z } from 'zod';
import { PLATFORM_SETTINGS, resolveSetting } from './quiz-rules.js';
/**
 * Un dominio de correo, normalizado.
 *
 * Sin arroba y en minúsculas: `institut.cat`, no `@institut.cat` ni `Institut.CAT`. La comparación
 * es por **igualdad exacta**, nunca por sufijo: con sufijo, `midominio.cat` dejaría entrar a
 * `nomidominio.cat`.
 */
export const roomDomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    // Se admite escrito con arroba porque es como lo teclea todo el mundo, y se quita.
    .transform((valor) => valor.replace(/^@/, ''))
    .refine((valor) => /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(valor), {
    message: 'No parece un dominio',
});
/** Cuántos dominios caben. Más que eso no es una clase, es una lista de distribución. */
export const MAX_ROOM_DOMAINS = 8;
export const roomSettingsSchema = z.object({
    /**
     * Quién puede entrar, por el dominio de su correo.
     *
     * **Vacío significa todo el mundo**, que es el valor por defecto y el que pide el sentido
     * común: una sala nueva no restringe nada. Es distinto del filtro de instancia
     * (`ALLOWED_EMAIL_DOMAINS`), que decide quién es usuario de Clica; este decide quién entra en
     * ESTA clase.
     */
    allowedDomains: z.array(roomDomainSchema).max(MAX_ROOM_DOMAINS),
    /**
     * Barajar las opciones **para cada alumno**.
     *
     * `null` = lo que diga el cuestionario. Es lo único que puede barajarse por persona: el orden
     * de las preguntas lo marca el proyector, que enseña una pregunta común a toda la clase.
     */
    shuffleOptions: z.boolean().nullable(),
    /** Si se admiten apodos. Vivía suelto en el estado de la partida y se perdía en cada reinicio. */
    allowAlias: z.boolean(),
});
/** Una sala nueva no restringe nada y hereda todo lo que puede heredar. */
export const DEFAULT_ROOM_SETTINGS = {
    allowedDomains: [],
    shuffleOptions: null,
    allowAlias: PLATFORM_SETTINGS.allow_alias,
};
/**
 * Resuelve el barajado con los cuatro niveles: sala → pregunta → cuestionario → plataforma.
 *
 * La sala solo interviene si dice algo. Se separa de `resolveSetting` en vez de añadirle un
 * parámetro porque no es lo mismo: los tres niveles de abajo son la configuración del material y
 * viven en la base de datos; este es una decisión de esta clase concreta, de hoy.
 */
export function resolveShuffleOptions(room, question, quiz) {
    if (room.shuffleOptions !== null)
        return room.shuffleOptions;
    return resolveSetting('shuffle_options', question, quiz);
}
/**
 * ¿Puede entrar este correo en esta sala?
 *
 * **La lista vacía admite a todo el mundo sin mirar el correo**, y el orden de estas dos líneas
 * es la razón de que exista esta función en vez de reutilizar `isEmailDomainAllowed` tal cual:
 * aquella devuelve `false` cuando no puede determinar el dominio, **incluso sin restricciones
 * configuradas**. Ese orden es el correcto donde vive —el alta de una cuenta, donde un correo
 * indeterminable sí debe rechazarse—, pero aquí dejaría fuera a quien tiene el perfil anonimizado
 * de una sala que no restringe nada.
 */
export function isAllowedInRoom(email, settings) {
    if (settings.allowedDomains.length === 0)
        return true;
    const dominio = domainOfEmail(email);
    if (dominio === null)
        return false;
    return settings.allowedDomains.includes(dominio);
}
/** El dominio de un correo, en minúsculas. Nulo si no hay forma de saberlo. */
export function domainOfEmail(email) {
    if (email === null || email === undefined)
        return null;
    const arroba = email.lastIndexOf('@');
    if (arroba <= 0 || arroba === email.length - 1)
        return null;
    return email.slice(arroba + 1).toLowerCase();
}
//# sourceMappingURL=room-settings.js.map